/**
 * Root layout component providing application structure and authentication context.
 *
 * Handles the main application layout with header, navigation, authentication flows,
 * error boundaries, and route context tracking for error reporting.
 */

import { Security } from '@okta/okta-react';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import { AuthNav } from '../features/auth/components/AuthNav';
import { useUserProfile } from '../features/auth/hooks/useUserProfile';
import oktaAuth from '../features/auth/services';
import { errorService } from '../lib/errorService';
import {
  SentryErrorBoundary,
  clearSentryUserContext,
  convertSearchParamsForSentry,
} from '../lib/sentry';
import { ErrorFallback } from './ErrorFallback';

/**
 * Layout content that runs inside the Security provider.
 * This is where we can safely use Okta hooks like useUserProfile.
 */
function LayoutContent() {
  const location = useLocation();
  const search = useSearch({ strict: false });
  const { userProfile } = useUserProfile(); // Safe to use here - inside Security provider

  // Set route context for error reporting with Sentry
  useEffect(() => {
    const searchParams = convertSearchParamsForSentry(search);
    errorService.setRouteContext(location.pathname, searchParams);
  }, [location.pathname, search]);

  // Clear user context from Sentry when user logs out
  useEffect(() => {
    if (userProfile === null) {
      clearSentryUserContext();
    }
  }, [userProfile]);

  // Pages that should not show the application title (footer-linked pages)
  const hideApplicationTitle = ['/', '/contact', '/terms'].includes(
    location.pathname
  );

  return (
    <>
      {/* Sticky Header: Logo and AuthNav */}
      <header className='p-4 bg-background border-b border-border sticky top-0 z-50 sm:px-4 sm:py-2'>
        <nav
          className='container mx-auto flex justify-between items-center'
          aria-label='Main navigation'
        >
          <Link to='/' className='flex items-center space-x-2'>
            <img
              src='/tca_logo_dark.webp'
              alt='Total Care Auto Logo'
              className='h-14 w-auto -mb-5'
            />
          </Link>
          <AuthNav />
        </nav>
      </header>

      {/* Application Title Area (Not Sticky) - Hidden on some pages */}
      {!hideApplicationTitle && (
        <div className='py-3'>
          <div className='container mx-auto text-center'>
            <h1 className='text-2xl font-semibold text-foreground'>
              TCA Lender Quote Portal
            </h1>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className='container mx-auto p-4'>
        <Outlet />
      </main>

      <Toaster richColors closeButton />

      <footer className='p-4 bg-muted border-t text-sm'>
        <div className='container mx-auto flex flex-col space-y-3'>
          {/* Top Row: Useful Links */}
          <div className='flex justify-end items-center space-x-6'>
            <Link
              to='/contact'
              className='text-muted-foreground hover:text-foreground hover:underline transition-colors'
            >
              Contact Us
            </Link>
            <Link
              to='/terms'
              className='text-muted-foreground hover:text-foreground hover:underline transition-colors'
            >
              Terms & Conditions
            </Link>
          </div>

          {/* Bottom Row: Logo (Left) and Copyright (Right) */}
          <div className='flex justify-between items-end'>
            {/* Left: Company Logo */}
            <img
              src='/tca_logo_dark.webp'
              alt='Total Care Auto Logo'
              className='h-14 w-auto'
            />

            {/* Right: Copyright */}
            <p className='text-muted-foreground'>
              &copy; {new Date().getFullYear()} Total Care Auto, Powered by
              Landcar. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}

/**
 * Layout component wrapped with authentication provider.
 */
function LayoutWithAuth() {
  const navigate = useNavigate();

  /**
   * Restore original URI after Okta authentication callback.
   * Handles navigation back to the intended route after successful login.
   */
  const restoreOriginalUri = async (
    _oktaAuth: unknown,
    originalUri: string | undefined
  ) => {
    if (!originalUri) {
      navigate({ to: '/', replace: true });
      return;
    }

    // Simply navigate to the originalUri as-is, let TanStack Router handle parsing
    window.history.replaceState({}, '', originalUri);
    window.location.reload();
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <LayoutContent />
    </Security>
  );
}

/**
 * Main root layout component with error boundary protection.
 */
const LayoutWithErrorBoundary = SentryErrorBoundary(LayoutWithAuth, {
  fallback: ErrorFallback,
  showDialog: true,
});

/**
 * Root layout component exported to TanStack Router.
 * Entry point for the application layout with error boundary and auth protection.
 */
export function RootLayout() {
  return <LayoutWithErrorBoundary />;
}
